import React from "react";
import { useSelector } from "react-redux";
import {
  Divider,
  Fade,
  FormControl,
  makeStyles,
  Modal,
  Paper,
} from "@material-ui/core";
import { Close } from "@material-ui/icons";
import {
  Button,
  Checkbox,
  Select,
  MenuItem,
  FormControlLabel,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";

import {
  updateUserSettingsVisibilityInStore,
  updateUserSettingsUserIdInStore,
  setUsersUpdatedInManagementInStore,
}
from "../../../components/common/StoreSettingsUi";
import {
  addSnackbarIssue,
} from "../../../components/common/StoreCommon";
import {
  getShowUserSettings,
  getUserSettingsUserId,
}
from "../../../store/ui/settings";
import { getTeams } from "../../../store/hub/teams";
import UserAutoComplete from "./components/UserAutoComplete";
import {
  getUserProfile,
  updateSimpleUserProfile,
} from "../../../components/common/ApiMiddleware";

export default function UserManagement() {
  const classes = useStyles();
  //const [userLoaded, setUserLoaded] = React.useState(false);
  const showUserSettings = useSelector(getShowUserSettings)();
  const open = Boolean(showUserSettings);
  
  const selectedUserId = useSelector(getUserSettingsUserId)();
  const teams = useSelector((state) => getTeams(state)()) ?? [];
  const [localUserId, setLocalUserId] = React.useState(-1);
  const [firstName, setFirstName] = React.useState(null);
  const [lastName, setLastName] = React.useState(null);
  const [disabled, setDisabled] = React.useState(false);
  const [primaryTeamId, setPrimaryTeamId] = React.useState("");
  
  //const tokenHelper = new TokenHelper();
  //const isAdmin = tokenHelper.isAdminRoleSet();

  const saveUserProfile = () => {
    if (selectedUserId) {
      updateSimpleUserProfile(
        JSON.stringify({
          Id: selectedUserId,
          FirstName: firstName,
          LastName: lastName,
          DisabledFl: disabled,
          PrimaryTeamId: Number(primaryTeamId),
        })
      ).then(() => {
        setUsersUpdatedInManagementInStore(true);
        addSnackbarIssue("User updated", "success");
      }).catch(() => {
        addSnackbarIssue("An error occurred updating the user please try again.","warning");
      }
      );
    }
  };

  const close = () => {
    updateUserSettingsUserIdInStore(null);
    updateUserSettingsVisibilityInStore(false);
  }

  React.useEffect(() => {
    if (!selectedUserId) {
      setFirstName('');
      setLastName('');
      setDisabled(false);
      setPrimaryTeamId('');
      setLocalUserId(-1);
      return;
    }
    if (selectedUserId === localUserId) return;

    let cancelled = false;
    getUserProfile(selectedUserId).then(
      (res) => {
        if (cancelled) return;
        const user = typeof res === "string" ? JSON.parse(res) : res;
        if (user && user.error) {
          updateUserSettingsVisibilityInStore(false);
          return;
        }
        setFirstName(user.FirstName ?? "");
        setLastName(user.LastName ?? "");
        setDisabled(Boolean(user.DisabledFl));
        // Primary team: UserRO.UserTeams (UserUserTeamRO[]) -> find IsPrimary, then UserTeam.Id
        const userTeams = user.UserTeams;
        const primaryUserTeam = Array.isArray(userTeams) ? userTeams.find((ut) => ut.IsPrimary === true) : null;
        const userTeam = primaryUserTeam && (primaryUserTeam.UserTeam);
        const teamId = userTeam ? (userTeam.Id ?? userTeam.id) : null;
        setPrimaryTeamId(teamId != null && teamId !== "" ? String(teamId) : "");
        setLocalUserId(selectedUserId);
      }
    );
    return () => { cancelled = true; };
  }, [selectedUserId, localUserId]);

  const inputProps = {
    style: {
      height: 40,
    },
  };

  return (
    <Modal
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        alignContent: "center",
      }}
      anchorEl={showUserSettings}
      open={open}
      BackdropProps={{
        timeout: 500,
        style: { background: "rgba(50,50,50,0.5)" },
      }}
      aria-labelledby="User Management"
      aria-describedby="Update users here"
    >
      <Fade in={open}>
        <Paper className={classes.paper}>
          <Box className={classes.row}>
            <Typography variant="body1">User Management</Typography>
            <IconButton
              style={{ width: 30, height: 30 }}
              onClick={() => close()}
            >
              <Close />
            </IconButton>
          </Box>

          <Box style={{ margin: "10px 0" }}>
            <UserAutoComplete />
          </Box>
    
          <Divider />
        
          <Box style={{ margin: "10px 0", width: "100%" }}>
            <Typography>
                User Details
              </Typography>
          </Box>

          <Box 
            component="form"
            sx={{
              '& > :not(style)': { m: 0.75 },
            }}
            noValidate
            autoComplete="off">

            <TextField
              fullWidth 
              InputProps={inputProps}
              type={"text"}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              label="First Name"
              InputLabelProps={{ shrink: true }}
            />
      
            <TextField
              fullWidth 
              InputProps={inputProps}
              type={"text"}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              label="Last Name"
              InputLabelProps={{ shrink: true }}
            />

            <Box>
              <Typography component="label" htmlFor="user-management-primary-team-select" variant="caption" color="textSecondary" display="block" sx={{ mb: 0.5 }}>
                Primary Team
              </Typography>
              <FormControl fullWidth variant="outlined" sx={{ "& .MuiOutlinedInput-root": { height: 40 } }}>
                <Select
                  id="user-management-primary-team-select"
                  value={primaryTeamId ?? ""}
                  onChange={(e) => setPrimaryTeamId(e.target.value === "" ? "" : String(e.target.value))}
                  displayEmpty
                  renderValue={(v) => v === "" ? <em>None</em> : (teams.find((t) => String(t.Id) === v)?.Name ?? v)}
                  inputProps={{ ...inputProps, "aria-label": "Primary Team" }}
                  sx={{ height: 40 }}
                  MenuProps={{
                  disableScrollLock: true,
                  PaperProps: {
                    style: { maxHeight: 320 },
                    sx: {
                      "& ul": { display: "block", flexDirection: "unset" },
                      "& .MuiMenuItem-root": { display: "block", whiteSpace: "nowrap" },
                    },
                  },
                  MenuListProps: {
                    style: { display: "block", padding: 0 },
                    sx: { display: "block" },
                  },
                }}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {teams.map((team) => (
                  <MenuItem key={team.Id} value={String(team.Id)}>
                    {team.Name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={disabled}
                  onChange={(e) => setDisabled(!disabled)}
                  name="cbDisabled"
                />
              }
              label="Disabled"
            />

            <Box textAlign='center'>

              <Button 
                sx={{
                  width: 100,
                  
                }}
                onClick={() => saveUserProfile()}
                variant="contained"
                >
                Save
              </Button>
            </Box>
          </Box>

          
        </Paper>
      </Fade>
    </Modal>
  );
}

const useStyles = makeStyles((theme) => ({
  paper: {
    background: "white",
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    overflow: "auto",
    [theme.breakpoints.only("xs")]: {
      width: "100vw",
      height: "100vh",
    },
    [theme.breakpoints.up("sm")]: {
      borderRadius: 10,
      width: "500px",
      height: "auto",
    },
  },
  row: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
}));
